import { Capacitor } from '@capacitor/core';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { setApiBase } from './api';

export type ConnectionMode = 'cloud' | 'local';
export interface ConnectionPreference {
  mode: ConnectionMode;
  lanHost: string;
  lanPort: number;
}

const STORAGE_KEY = 'network-mode';
const STORAGE_PREFIX = 'cok-operations';
const DEFAULT: ConnectionPreference = { mode: 'cloud', lanHost: '192.168.1.10', lanPort: 8080 };
const BUILD_ENV = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};
const CLOUD_API = (BUILD_ENV.VITE_API_BASE || (Capacitor.isNativePlatform()
  ? 'https://sornix-erp-api.sornix-erp.workers.dev'
  : '')).replace(/\/$/, '');
let preference: ConnectionPreference = { ...DEFAULT };
let initialized = false;
let nativeStoreReady = false;

function apiFor(value: ConnectionPreference): string {
  if (value.mode === 'cloud') return CLOUD_API;
  const host = validateLanHost(value.lanHost);
  const port = validatePort(value.lanPort);
  return `http://${host}:${port}`;
}

export function validateLanHost(input: string): string {
  const host = input.trim().replace(/^\[|\]$/g, '');
  if (!host || host.includes('://') || /[\s/@?#]/.test(host) || host.includes(':')) {
    throw new Error('Enter a LAN hostname or IPv4 address only (no scheme, path or credentials).');
  }
  const octets = host.split('.');
  const isIpv4 = octets.length === 4 && octets.every((part) => /^\d{1,3}$/.test(part) && Number(part) <= 255);
  if (isIpv4) {
    const [a, b] = octets.map(Number);
    const privateAddress = a === 10 || a === 192 && b === 168 || a === 172 && b >= 16 && b <= 31 || a === 127;
    if (!privateAddress) throw new Error('Local mode accepts private LAN IPv4 addresses only.');
    return host;
  }
  if (octets.length > 1 && !/\.(local|lan|internal)$/i.test(host)) {
    throw new Error('Use a private .local/.lan/.internal hostname or a single-label site hostname.');
  }
  if (!/^[a-z0-9](?:[a-z0-9.-]{0,251}[a-z0-9])?$/i.test(host) || host.includes('..')) {
    throw new Error('Enter a valid on-site hostname.');
  }
  return host;
}

export function validatePort(value: number | string): number {
  const port = Number(value);
  if (!Number.isInteger(port) || port < 1 || port > 65535) throw new Error('Port must be an integer from 1 to 65535.');
  return port;
}

async function prepareSecureStore(): Promise<void> {
  if (!Capacitor.isNativePlatform() || nativeStoreReady) return;
  await SecureStorage.setKeyPrefix(STORAGE_PREFIX);
  nativeStoreReady = true;
}

export async function initializeConnection(): Promise<ConnectionPreference> {
  if (initialized) return { ...preference };
  try {
    await prepareSecureStore();
    const stored = Capacitor.isNativePlatform()
      ? await SecureStorage.get(STORAGE_KEY, false)
      : localStorage.getItem(`sornix.${STORAGE_KEY}`);
    const parsed = typeof stored === 'string' ? JSON.parse(stored) as Partial<ConnectionPreference> : stored as Partial<ConnectionPreference> | null;
    if (parsed && (parsed.mode === 'cloud' || parsed.mode === 'local')) {
      preference = { ...DEFAULT, ...parsed };
      if (preference.mode === 'local') {
        preference.lanHost = validateLanHost(preference.lanHost);
        preference.lanPort = validatePort(preference.lanPort);
      }
    }
  } catch (error) {
    // If secure storage is unavailable, fail closed to Cloud/default rather than
    // silently putting native network preferences into unencrypted localStorage.
    preference = { ...DEFAULT };
    console.warn('[COK] secure connection preferences unavailable; using Cloud default', error);
  }
  setApiBase(apiFor(preference));
  initialized = true;
  return { ...preference };
}

export function getConnectionPreference(): ConnectionPreference {
  return { ...preference };
}

export async function saveConnectionPreference(next: ConnectionPreference): Promise<ConnectionPreference> {
  const normalized: ConnectionPreference = {
    mode: next.mode,
    lanHost: validateLanHost(next.lanHost),
    lanPort: validatePort(next.lanPort),
  };
  const apiBase = apiFor(normalized); // validate before persisting
  await prepareSecureStore();
  const value = JSON.stringify(normalized);
  if (Capacitor.isNativePlatform()) await SecureStorage.set(STORAGE_KEY, value);
  else localStorage.setItem(`sornix.${STORAGE_KEY}`, value);
  preference = normalized;
  initialized = true;
  setApiBase(apiBase);
  return { ...preference };
}

export function getConnectionLabel(): string {
  if (preference.mode === 'cloud') return 'Cloud · HTTPS';
  return `Local LAN · ${preference.lanHost}:${preference.lanPort}`;
}
